import { Button, ImageUpload, InlineMessage, Modal, ModalContent } from '@olytic/foundation';
import { type RefObject, useEffect, useState } from 'react';

import { photoInputError } from './profileModel';
import { useMyProfile } from './ProfileProvider';

/**
 * Your photo, opened from the user menu, as in studio-fripp. The library
 * cropper frames the picked image and exports a 512px WebP, which uploads at
 * once; removing asks first.
 */
export function YourPhotoDialog({
  open,
  onClose,
  returnFocusRef,
}: {
  open: boolean;
  onClose(): void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}): JSX.Element {
  const { avatarUrl, uploadPhoto, removePhoto } = useMyProfile();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setConfirmingRemove(false);
    }
  }, [open]);

  async function run(action: () => Promise<void>): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Your photo was not saved.');
    } finally {
      setSaving(false);
    }
  }

  function picked(_preview: string | undefined, file?: File): void {
    if (file === undefined) {
      setConfirmingRemove(true);
      return;
    }
    const problem = photoInputError(file);
    if (problem !== null) {
      setError(problem);
      return;
    }
    setConfirmingRemove(false);
    void run(() => uploadPhoto(file));
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <ModalContent
        description="Shown in the top bar and on your profile."
        onCloseAutoFocus={(event) => {
          // The menu item that opened this is gone; return to the menu's trigger.
          event.preventDefault();
          returnFocusRef?.current?.focus();
        }}
        size="sm"
        title="Your photo"
      >
        <div className="flex flex-col gap-3">
          <ImageUpload
            accept="image/png,image/jpeg,image/webp"
            crop={{ aspect: 1, outputWidth: 512, title: 'Frame your photo' }}
            disabled={saving}
            hint="PNG, JPEG or WebP. You frame it before it uploads."
            inputLabel="Choose photo"
            maxSizeMb={10}
            onChange={picked}
            previewAlt="Your photo"
            removable={avatarUrl !== undefined && !confirmingRemove}
            shape="circle"
            size={160}
            value={avatarUrl}
          />
          {error === null ? null : (
            <InlineMessage role="alert" tone="error">
              {error}
            </InlineMessage>
          )}
          {confirmingRemove ? (
            <InlineMessage
              aria-label="Confirm photo removal"
              className="flex flex-wrap items-center gap-2"
              role="group"
              tone="neutral"
            >
              <span className="w-full">Remove your photo?</span>
              <Button
                disabled={saving}
                onClick={() =>
                  void run(async () => {
                    await removePhoto();
                    setConfirmingRemove(false);
                  })
                }
                size="sm"
                type="button"
                variant="brand"
              >
                Remove
              </Button>
              <Button
                disabled={saving}
                onClick={() => setConfirmingRemove(false)}
                size="sm"
                type="button"
                variant="neutral"
              >
                Cancel
              </Button>
            </InlineMessage>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  );
}
